import { useListSecurityEvents, type SecurityDecision, type SecurityEventRow } from '@pa/api/admin'
import { DateRangeFilter, FilterMenu, fmtDateTimeOf, Pager, PAGE_SIZE, timeAgo, todayIn } from '@pa/ui'
import { ShieldCheck, Filter } from 'lucide-react'
import { useId } from 'react'
import { actionOfRoute, decisionLabel, DECISION_LABELS } from '../model/labels'
import { EVENT_PRESETS, eventRangeValue, hasEventFilters, toEventParams, type ProtectionSearch } from '../model/search'

export interface SecurityEventsProps {
  clinicId: string
  timeZone: string
  search: ProtectionSearch
  onSearch: (next: ProtectionSearch, opts?: { replace?: boolean }) => void
}

const DECISION_OPTIONS = (Object.keys(DECISION_LABELS) as SecurityDecision[]).map((d) => ({
  value: d,
  label: DECISION_LABELS[d],
}))

/** The decision cell: "would have" rows are muted (Monitor mode, or a rule set to monitor). */
function Decision({ e }: { e: SecurityEventRow }) {
  const label = decisionLabel(e.decision, e.mode)
  const tone =
    e.decision === 'challenge_passed'
      ? 'tag-blue'
      : (e.decision === 'block' && e.mode === 'enforce') || e.decision === 'challenge_failed'
        ? 'tag-red'
        : e.decision === 'challenge' || e.decision === 'would_challenge'
          ? 'tag-amber'
          : ''
  return <span className={`tag ${tone}`.trim()}>{label}</span>
}

/**
 * S-04 Protection's read-only list of recent security events (UserFlows S-04 Protection; API.md §7.16; Frontend §7.2):
 * time (clinic zone), action, decision ("Would have challenged" in Monitor mode) and the checks that fired. Filters
 * decision and date; numbered pager. Never an IP address, browser details or a hash: the API never sends them.
 */
export function SecurityEvents({ clinicId, timeZone, search, onSearch }: SecurityEventsProps) {
  const id = useId()
  const today = todayIn(timeZone)
  const list = useListSecurityEvents(clinicId, toEventParams(search, timeZone, today), {
    query: { staleTime: 15_000 },
  })
  const page = list.data
  const items = page?.items ?? []
  const filtered = hasEventFilters(search)
  const set = (patch: Partial<ProtectionSearch>) => {
    const merged: Record<string, unknown> = { ...search, ...patch, page: undefined }
    onSearch(Object.fromEntries(Object.entries(merged).filter(([, v]) => v !== undefined)))
  }

  return (
    <section className="card card-fill" aria-labelledby={`${id}-title`}>
      <div className="card-head">
        <h2 className="form-section-title" id={`${id}-title`}>
          Recent security events
        </h2>
      </div>
      <div className="toolbar filters">
        <FilterMenu
          label="Decision"
          icon={<Filter aria-hidden />}
          value={search.decision ?? null}
          allLabel="All decisions"
          options={DECISION_OPTIONS}
          onChange={(v) => {
            set({ decision: (v ?? undefined) as SecurityDecision | undefined })
          }}
        />
        <DateRangeFilter
          label="Date"
          value={eventRangeValue(search)}
          presets={EVENT_PRESETS}
          today={today}
          onChange={(r) => {
            set(
              r.id === 'any'
                ? { range: undefined, from: undefined, to: undefined }
                : { range: r.id, from: r.from, to: r.to },
            )
          }}
        />
        {filtered && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              onSearch({})
            }}
          >
            Clear filters
          </button>
        )}
      </div>
      <div className="table-wrap" aria-busy={list.isFetching}>
        {list.isError ? (
          <div className="empty" role="alert">
            <ShieldCheck aria-hidden />
            <div>Couldn&apos;t load the security events.</div>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => {
                void list.refetch()
              }}
            >
              Retry
            </button>
          </div>
        ) : list.isPending ? (
          <div className="empty" role="status">
            <span className="spinner" aria-hidden />
            <div>Loading security events…</div>
          </div>
        ) : items.length === 0 ? (
          <div className="empty">
            <ShieldCheck aria-hidden />
            <div>{filtered ? 'No security events match these filters' : 'No security events'}</div>
          </div>
        ) : (
          <table className="table rtable security-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Action</th>
                <th>Decision</th>
                <th>Checks that fired</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id}>
                  <td className="c-time" style={{ whiteSpace: 'nowrap' }}>
                    <b style={{ fontWeight: 600 }}>{fmtDateTimeOf(e.occurredAt, timeZone)}</b>
                    <span className="cell-sub">{timeAgo(e.occurredAt)}</span>
                  </td>
                  <td className="c-action" data-label="Action">
                    {actionOfRoute(e.route)}
                  </td>
                  <td className="c-decision" data-label="Decision">
                    <Decision e={e} />
                  </td>
                  <td className="c-rules" data-label="Checks that fired">
                    <span className="mono rules">{e.rules.join(', ') || '—'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {!list.isError && (
        <Pager
          page={page?.page ?? search.page ?? 1}
          pageSize={page?.pageSize ?? PAGE_SIZE}
          total={page?.total ?? 0}
          noun="events · newest first"
          onPage={(n) => {
            const next: ProtectionSearch = { ...search }
            if (n > 1) next.page = n
            else delete next.page
            onSearch(next)
          }}
        />
      )}
    </section>
  )
}
